import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import { IFieldValue, updateFieldValue } from 'Scripts/Slices/loginPage';
import { IRootState } from 'Scripts/store';

import { useForgotPasswordMutation } from 'Services/loginApi';

import CtaButton from 'Components/CtaButton';
import FormBox from 'Components/Forms/FormBox';
import RenderFormField, {
    handleFormPost,
    validateFormFields,
    ValidationSchema,
} from 'Components/Forms/RenderFormField';
import Markup from 'Components/Markup';

const ForgotPasswordPage = () => {
    const dispatch = useDispatch();
    const [formValidation, setFormValidation] = useState<ValidationSchema>([]);

    const [
        forgotPassword,
        {
            isLoading: forgotPasswordResultIsLoading,
            error: forgotPasswordResultError,
            data: forgotPasswordResultData,
        },
    ] = useForgotPasswordMutation();

    const {
        inputFields,
        canRequestNewPassword,
        sendPasswordButton,
        forgottenPasswordText,
        forgottenPasswordTitle,
        loginLink,
        title,
        channelUrl,
        translations,
        backToLoginPageLinkText,
        linkHasExpired,
    } = useSelector((state: IRootState) => state.loginPage.forgotPassword);

    const handleInputChange = (data: IFieldValue) => {
        dispatch(updateFieldValue({ data, key: 'forgotPassword' }));
    };

    const handleOnsubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (validateFormFields(inputFields, setFormValidation)) {
            forgotPassword({ form: e.currentTarget });
        }
    };

    useEffect(() => {
        if (linkHasExpired) {
            toast.error(translations.linkIsExpired);
        }
    }, []);

    useEffect(() => {
        handleFormPost(forgotPasswordResultData, forgotPasswordResultError);
    }, [forgotPasswordResultData, forgotPasswordResultError]);

    return (
        <div className="h-full w-full flex-grow bg-white">
            <div className="mx-auto max-w-content pb-4 lg:px-14 ">
                <div className="mx-auto mt-9 w-full md:max-w-form">
                    <h1 className="mx-4.5 font-montserrat text-2xl font-medium text-black md:mx-4.5 md:mb-4 lg:mx-7">
                        {title}
                    </h1>
                    {canRequestNewPassword ? (
                        <div>
                            <FormBox loading={forgotPasswordResultIsLoading}>
                                <h2 className="mb-2.5 font-montserrat text-xl font-medium text-black">
                                    {forgottenPasswordTitle}
                                </h2>
                                <Markup
                                    cssClass="mb-5"
                                    html={forgottenPasswordText?.value}
                                />
                                {!forgotPasswordResultData ? (
                                    <form
                                        onSubmit={e => handleOnsubmit(e)}
                                        className="md:max-w-md"
                                        method="post"
                                    >
                                        <ul className="flex flex-col">
                                            {inputFields.map((field, index) => (
                                                <RenderFormField
                                                    key={index}
                                                    loading={
                                                        forgotPasswordResultIsLoading
                                                    }
                                                    field={field}
                                                    handleInputChange={
                                                        handleInputChange
                                                    }
                                                    form={inputFields}
                                                    validation={formValidation}
                                                    as="li"
                                                />
                                            ))}
                                            <li>
                                                <CtaButton
                                                    disabled={
                                                        forgotPasswordResultIsLoading
                                                    }
                                                    className="mb-3 w-full py-2.5 text-xl font-medium"
                                                >
                                                    {sendPasswordButton}
                                                </CtaButton>
                                                <a href={loginLink.href}>
                                                    {backToLoginPageLinkText}
                                                </a>
                                            </li>
                                        </ul>
                                    </form>
                                ) : (
                                    <div className="flex flex-col md:max-w-md">
                                        <p className="mb-5">
                                            {forgotPasswordResultData.message}
                                        </p>
                                        <CtaButton
                                            className="mb-3 w-full py-2.5 text-xl font-medium"
                                            as="a"
                                            href={loginLink.href}
                                            type="button"
                                        >
                                            {backToLoginPageLinkText}
                                        </CtaButton>
                                    </div>
                                )}
                            </FormBox>
                        </div>
                    ) : (
                        <div className="md:max-w-md">
                            <FormBox>
                                <p className="mb-5">
                                    {translations.loginUserAlreadyLoggedInText}
                                </p>
                                <CtaButton
                                    className="w-full py-2.5 text-xl font-medium"
                                    as="a"
                                    href={`${channelUrl}.Logout`}
                                >
                                    {translations.logoutButtonText}
                                </CtaButton>
                            </FormBox>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
